/**
 * VedcoolMark - the Vedcool book/"e" favicon, hand-traced to SVG.
 *
 * Geometry was extracted pixel-by-pixel from public/favicon.png (231x231) so the
 * vector matches the raster exactly. The viewBox is kept at 0 0 231 231 to keep
 * those raw coordinates readable against the source.
 *
 * The mark is drawn as three separately-addressable pieces rather than one path
 * so the splash animation can stagger them:
 *   - `body`      the outer book silhouette with the "e" counter knocked out
 *   - `earm`      the horizontal bar of the "e" (a knockout, drawn in white)
 *   - `bookmark`  the ribbon on the lower cover
 *
 * Every element takes an id/className from props so the animation layer can
 * target them without reaching through DOM structure.
 */

const GREEN = '#0BB06C';

export default function VedcoolMark({
  className = '',
  bodyClassName = '',
  spineFillClassName = '',
  spineStrokeClassName = '',
  bookmarkClassName = '',
  eWipeClassName = '',
  /** Renders the white wiper rect used by the splash to reveal the "e" bar. */
  withEWipe = false,
  title = 'Vedcool',
  ...rest
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 231 231"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      {...rest}
    >
      {/*
        Outer book silhouette. Traced clockwise from the top-left arc.
        The `evenodd` fill rule lets the "e" counter and the spine hollow be
        expressed as reversed sub-paths inside the same shape.
      */}
      <path
        className={bodyClassName}
        fill={GREEN}
        fillRule="evenodd"
        clipRule="evenodd"
        d="
          M 40 6
          L 211 6
          C 218 6 223 11 223 18
          L 223 133
          C 223 140 218 145 211 145
          L 40 145
          C 21 145 6 130 6 111
          L 6 40
          C 6 21 21 6 40 6
          Z

          M 84 38
          C 71 38 62 47 62 60
          L 62 110
          L 185 110
          L 185 84
          L 94 84
          L 94 70
          L 185 70
          L 185 38
          Z
        "
      />

      {/*
        Lower cover + spine, drawn as its own shape so the splash animation can
        stagger it behind the upper cover.

        Measuring the source rows 146-205, the green runs from x=6 out to a
        right edge that tapers 49 -> 23 -> 50, symmetric about y=176. That is
        the rounded end-cap of a thick stroke, not a filled outline - so this is
        drawn as a stroked centreline with round caps/joins, which reproduces
        the taper exactly and keeps the path readable.

        Spine and closing bar are separate strokes because they are different
        weights: the spine spans x=6..41 (35 wide) while the bottom bar spans
        y=206..223 (17 wide).

        The spine's right edge is filled rather than stroked, because it tapers
        (49 at y=146, 23 at y=176, 50 at y=205) where a stroke would stay flat.

        The closing edge must stop at y=205 and hand off to the rounded bar
        below it. Running it past the viewBox bottom clips the bottom-left into
        a sharp spur instead of the source's smooth curve, which sweeps inward
        from x=7 at y=195 to x=39 at y=223.

        The top of this path deliberately starts at y=96, well *under* the
        cover's lower edge (y=145) rather than meeting it. Abutting edges
        anti-alias independently and leave a white hairline along the left side
        during the splash, when the cover animates on its own layer. Overlapping
        is invisible - both shapes are the same solid green - and removes the
        seam by construction. Do not "tidy" this back to a shared edge.
      */}
      <path
        className={spineFillClassName}
        fill={GREEN}
        d="
          M 6 96
          C 16 136 32 146 50 146
          C 33 152 24 163 24 176
          C 24 189 33 200 50 205
          L 30 205
          C 30 205 22 204 14.6 196
          L 6 186
          Z
        "
      />
      <path
        className={spineStrokeClassName}
        fill="none"
        stroke={GREEN}
        strokeWidth="17.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="
          M 14.6 180
          C 14.6 200.5 23.6 214.7 43 214.7
          L 214 214.7
        "
      />

      {/* Bookmark ribbon on the lower cover, with its V notch. */}
      <path
        className={bookmarkClassName}
        fill={GREEN}
        d="
          M 57 163
          L 90 163
          L 90 195
          L 73.5 186
          L 57 195
          Z
        "
      />

      {/*
        Splash-only wiper. The "e" crossbar is a knockout in the cover path and
        so cannot be animated on its own; this white rect starts covering the
        counter and scales away to the left, which reads as the bar being
        drawn in. It is scaled from x=62 (the counter's left edge).
      */}
      {withEWipe && (
        <rect
          className={eWipeClassName}
          x="62"
          y="38"
          width="124"
          height="73"
          fill="#ffffff"
        />
      )}
    </svg>
  );
}
